import React, { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  Container,
  Divider,
  Grid,
  Header,
  Image,
  Segment,
} from "semantic-ui-react";
import MyTextInput from "../../app/common/form/MyTextInput";
import {
  completePasswordReset,
  verifyResetCode,
} from "../../app/firestore/firebaseService";

// Handles the link from the "Forgot Password" email (mode=resetPassword&oobCode=...).
// Firebase's default hosted page is bypassed via actionCodeSettings.handleCodeInApp
// in resetPassword(), so this page owns the whole verify -> reset -> success flow.
export default function ResetPasswordActionPage() {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  const [status, setStatus] = useState("verifying"); // verifying | ready | invalid | done
  const [email, setEmail] = useState(null);

  useEffect(() => {
    if (mode !== "resetPassword" || !oobCode) {
      setStatus("invalid");
      return;
    }
    verifyResetCode(oobCode)
      .then((verifiedEmail) => {
        setEmail(verifiedEmail);
        setStatus("ready");
      })
      .catch(() => setStatus("invalid"));
  }, [mode, oobCode]);

  return (
    <div className="background-lightblue-gradiant">
      <Container style={{ height: "100vh" }}>
        <Grid>
          <Grid.Row>
            <div className="medium top margin" style={{ width: "100%" }}>
              <Image
                src="/assets/logo-small.png"
                style={{ float: "left", width: "240px" }}
              />
            </div>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column
              className="large top margin"
              mobile={16}
              tablet={16}
              computer={7}
            >
              <Segment style={{ marginTop: "40px" }}>
                <Image
                  centered
                  src="/assets/logo-transactioner-color-small.png"
                  width="110"
                  alt="TransActioner"
                  style={{ marginTop: "16px" }}
                />

                {status === "verifying" && (
                  <Header
                    color="blue"
                    as="h2"
                    textAlign="center"
                    className="text-big small top margin"
                  >
                    Checking your link&hellip;
                  </Header>
                )}

                {status === "invalid" && (
                  <>
                    <Header
                      color="blue"
                      as="h1"
                      textAlign="center"
                      className="text-big small top margin"
                    >
                      Link Expired
                    </Header>
                    <Divider className="medium top margin large bottom margin" />
                    <p className="text-small" style={{ textAlign: "center" }}>
                      This password reset link is invalid or has expired.
                      Please request a new one from the login page.
                    </p>
                    <Button
                      as={Link}
                      to="/"
                      primary
                      fluid
                      size="large"
                      content="Back to Login"
                      style={{ marginTop: "16px" }}
                    />
                  </>
                )}

                {status === "ready" && (
                  <>
                    <Header
                      color="blue"
                      as="h1"
                      textAlign="center"
                      className="text-big small top margin"
                    >
                      Reset Your Password
                    </Header>
                    {email && (
                      <p
                        className="text-small"
                        style={{ textAlign: "center" }}
                      >
                        for {email}
                      </p>
                    )}
                    <Divider className="medium top margin large bottom margin" />
                    <Formik
                      initialValues={{ newPassword1: "", newPassword2: "" }}
                      validationSchema={Yup.object({
                        newPassword1: Yup.string()
                          .min(6, "Password must be at least 6 characters")
                          .required("Password is required"),
                        newPassword2: Yup.string()
                          .required("Password is required")
                          .oneOf(
                            [Yup.ref("newPassword1"), null],
                            "Passwords do not match"
                          ),
                      })}
                      onSubmit={async (values, { setSubmitting, setErrors }) => {
                        try {
                          await completePasswordReset(
                            oobCode,
                            values.newPassword1
                          );
                          setStatus("done");
                        } catch (error) {
                          setErrors({ auth: error.message });
                          setSubmitting(false);
                        }
                      }}
                    >
                      {({ errors, isSubmitting, isValid, dirty }) => (
                        <Form className="ui form">
                          <MyTextInput
                            name="newPassword1"
                            type="password"
                            placeholder="New Password"
                            autoComplete="new-password"
                          />
                          <MyTextInput
                            name="newPassword2"
                            type="password"
                            placeholder="Confirm Password"
                            autoComplete="new-password"
                          />
                          {errors.auth && (
                            <p style={{ color: "#9f3a38" }}>{errors.auth}</p>
                          )}
                          <Button
                            primary
                            fluid
                            size="large"
                            type="submit"
                            loading={isSubmitting}
                            disabled={!isValid || !dirty || isSubmitting}
                            content="Reset Password"
                          />
                        </Form>
                      )}
                    </Formik>
                  </>
                )}

                {status === "done" && (
                  <>
                    <Header
                      color="blue"
                      as="h1"
                      textAlign="center"
                      className="text-big small top margin"
                    >
                      Password Changed
                    </Header>
                    <Divider className="medium top margin large bottom margin" />
                    <p className="text-small" style={{ textAlign: "center" }}>
                      Your password has been updated. You can now sign in
                      with your new password.
                    </p>
                    <Button
                      as={Link}
                      to="/"
                      primary
                      fluid
                      size="large"
                      content="Log In"
                      style={{ marginTop: "16px" }}
                    />
                  </>
                )}
              </Segment>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Container>
    </div>
  );
}
